import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { useState } from 'react';
import StringTitle from './StringTitle';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Grid, GridItem, DateField, Message } from '@flos/react-ui';
import type { ExternaleCaseDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/policyjob/edi/dto';
import {
    convertDateToLocalDateDTO,
    convertLocalDateDTOToDate,
    isDateInThePast
} from '../../../utils/dateUtil';
import { externalCasesAtom } from '../../../recoil/atoms/externalCasesAtom';
import {
    updateExternalCaseDTOProperties,
    dateHasValidationErrors
} from '../../../utils/externalCasesUtil';
import EDIValidationErrorMessage from './EDIValidationErrorMessage';
import { useHippoText } from '../../../hooks/useHippoText';
import { sortedCoverablesListSelector } from '../../../recoil/selectors/sortedCoverablesListSelector';
import { shouldShowConditionReportWarning } from '../../../utils/coverablesUtil';
import type { LobStatusFieldsValidation } from '../steps/DatesAndCancellationStep';

const EDICancellationNo = ({
    externalCase,
    coverable,
    lobStatusFieldValidation
}: {
    externalCase: ExternaleCaseDTO;
    coverable: CoverableDTO;
    lobStatusFieldValidation: LobStatusFieldsValidation;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { startDate, caseId } = externalCase;
    const [externalCaseDTOList, setExternalCaseDTOList] = useRecoilState(externalCasesAtom);
    const { sortedCoverables } = useRecoilValue(sortedCoverablesListSelector);
    const [showErrorMessage, setShowErrorMessage] = useState(
        startDate ? dateHasValidationErrors(startDate, coverable, sortedCoverables, true) : false
    );
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    if (!caseId) return <></>;

    const onStartDateChange = (date: Date): void => {
        const startDate = convertDateToLocalDateDTO(date);
        const updatedExternalCaseProperties = updateExternalCaseDTOProperties(
            externalCaseDTOList,
            [caseId],
            {
                startDate
            }
        );
        setExternalCaseDTOList(updatedExternalCaseProperties);
        setShowErrorMessage(dateHasValidationErrors(startDate, coverable, sortedCoverables, true));
        setHasUnsavedChanges(true);
    };

    return (
        <Grid gap={7}>
            <GridItem span={12} className="u-spacing-stack-top-5">
                <StringTitle space={true}>
                    {getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-NO-CANCELLATION-SUBHEADER')}
                </StringTitle>
                <DateField
                    selected={
                        startDate && !isDateInThePast(convertLocalDateDTOToDate(startDate))
                            ? convertLocalDateDTOToDate(startDate)
                            : undefined
                    }
                    label="Vælg dato"
                    id={`no-datefield-${String(caseId)}`}
                    onChange={onStartDateChange}
                    minDate={new Date()}
                    dateFormat="dd. MMMM yyyy"
                    isValid={
                        lobStatusFieldValidation?.isStartDateValid === undefined ||
                        lobStatusFieldValidation.isStartDateValid ||
                        hasUnsavedChanges
                    }
                />
                {shouldShowConditionReportWarning(coverable) && (
                    <Message theme={'warning'} className="u-spacing-stack-m">
                        {getHippoText(
                            'DSS-EFFECTIVE-DATE-AND-CANCELLATION-WARNING-CONDITION-REPORT'
                        )}
                    </Message>
                )}
            </GridItem>

            {showErrorMessage && (
                <GridItem span={12}>
                    <EDIValidationErrorMessage coverable={coverable} isStartDate />
                </GridItem>
            )}
        </Grid>
    );
};

export default EDICancellationNo;
